import { EventDetails, EventWithCustomField } from "@ac-expo/api-types";
import { getBuyableItemGroupKey } from "@ac-expo/utils";
import { useMemo } from "react";
import { useEventAvailableEntries } from "./useEventAvailableEntries";
import { useLocalCart } from "./useLocalCart";
import { BuyableEntryTypeWithKey } from "./useTicketing";

export const useEmbedTicketing = ({
  event,
  additionalUserTypes = ["group", "exhibitor"],
}: {
  event: EventWithCustomField<EventDetails> | undefined;
  additionalUserTypes?: string[];
}) => {
  const {
    availableEntries,
    availableTicketTypes,
    availableBadgeTypes,
    availableActivityTicketTypes,
  } = useEventAvailableEntries({
    event,
    isProfessional: false,
    additionalUserTypes,
  });

  const localCart = useLocalCart(availableEntries);

  const buyableTicketTypes: BuyableEntryTypeWithKey<"ticket">[] = useMemo(
    () =>
      availableTicketTypes.map((ticketType) => ({
        entryType: ticketType,
        itemKey: getBuyableItemGroupKey({
          buyableType: "ticket",
          ticketType: { id: ticketType.id },
        }),
      })),
    [availableTicketTypes]
  );

  const buyableBadgeTypes: BuyableEntryTypeWithKey<"badge">[] = useMemo(
    () =>
      availableBadgeTypes.map((badgeType) => ({
        entryType: badgeType,
        itemKey: getBuyableItemGroupKey({
          buyableType: "badge",
          badgeType: { id: badgeType.id },
        }),
      })),
    [availableBadgeTypes]
  );

  const buyableActivityTicketTypes: BuyableEntryTypeWithKey<"activity">[] =
    useMemo(
      () =>
        availableActivityTicketTypes.map((ticketType) => ({
          entryType: ticketType,
          itemKey: getBuyableItemGroupKey({
            buyableType: "ticket",
            ticketType: { id: ticketType.id },
          }),
        })),
      [availableActivityTicketTypes]
    );

  return {
    localCart,
    buyableTicketTypes,
    buyableBadgeTypes,
    buyableActivityTicketTypes,
    availableTicketTypes,
    availableBadgeTypes,
    availableActivityTicketTypes,
  };
};

export type UseEmbedTicketingReturns = ReturnType<typeof useEmbedTicketing>;
